import React, { useEffect } from 'react';
import { useOutletContext } from 'react-router';
import ChipList from '@Components/ChipList';
import TextField from '@Components/TextField';
import Button from '@Components/Button';
import { Avatar } from '@mui/material';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import { getInitials } from '@Utilities/services/name';
import Alert from '@Components/Alert';
import CurrencyTextField from '@Components/CurrencyTextField';
import IconBanner from '@Components/IconBanner';
import GiftCircleImg from 'assets/images/gift_circle.svg';
import AppBar from '@Components/AppBar';
import { formatCurrency } from '@Utilities/services/currency';
import { useBrowserHistory } from '@Utilities/services/hooks/useBrowserHistory';
import { MAXIMUM_CHARACTER_COUNT } from 'components/Gift/services/constants';
import { logComponentEvent } from '@Utilities/services/logging/componentEvent';
import './styles/giftDetails.scss';

export default function GiftDetails() {
  const browserHistory = useBrowserHistory();
  const {
    locale,
    localized,
    beneficiary,
    giftOptions,
    checkoutMessage,
    isSubmitLoading,
    selectedGiftAmount,
    isAmountEmptyMessage,
    amountError,
    messageError,
    updateMessage,
    updateGiftAmount,
    selectedGiftOption,
    updateGiftOption,
    onDetailsNext,
    isGiftingOccasionEnabled,
    selectedOccasion
  } = useOutletContext();

  const { profilePictureUrl, firstName, lastInitial } = beneficiary || {};

  useEffect(() => {
    logComponentEvent(
      'GiftingAmount', 'PageLoaded',
      { data: { occasion: selectedOccasion && selectedOccasion.eventLabel || '' } }
    );
  }, []);

  return (
    <div className='gift-details'>
      <AppBar
        title={localized['giftDetails.title'] || ''}
        onBackClick={() => {
          browserHistory.push(`/gift${isGiftingOccasionEnabled ? '/occasion' : ''}`);
        }}
        className='gift-details__toolbar'
        showBackButton
      />
      <IconBanner
        title={localized['giftDetails.banner.title'] || ''}
        className='gift-details__banner'
        description={localized['giftDetails.banner.description'] || ''}
        imgSrc={GiftCircleImg}
        imgAlt={localized['img.alt.giftCircle']}
      />
      {selectedOccasion && (
        <img
          src={selectedOccasion?.cardImage || ''}
          alt={selectedOccasion?.cardImageAlt || ''}
          className='gift-details__occasion-card'
        />
      )}
      <section className='gift-details__amount'>
        <div className='gift-details__amount__avatar'>
          {profilePictureUrl ? (
            <Avatar
              alt={getInitials(firstName, lastInitial)}
              src={profilePictureUrl}
            />
          ) : (
            <Avatar>{getInitials(firstName, lastInitial) || <PermIdentityIcon />}</Avatar>
          )}
        </div>
        <p className='gift-details__amount__total'>{formatCurrency(selectedGiftAmount, locale)}</p>
        <div className='gift-details__amount__input'>
          <span className='gift-details__amount__input__label'>
            {localized['giftDetails.amount.input.label'] || ''}
          </span>

          <div className='gift-details__amount__input__container'>
            {amountError?.message === 'giftDetails.error.amountEmpty' && (
              <Alert
                title={localized[amountError.message]}
                severity={amountError.severity}
                className='gift-details__amount__input__container__alert'
              />
            )}
            <div className='gift-details__amount__input__container__options'>
              <ChipList
                onChange={updateGiftOption}
                options={giftOptions}
                selectedValue={selectedGiftOption}
                error={amountError?.message}
                showErrorMessage={false}
                localized={localized}
              />
              {selectedGiftOption === 'custom' && (
                <CurrencyTextField
                  localized={localized}
                  className='gift-details__amount__input__container__options__custom-input'
                  value={selectedGiftAmount}
                  error={!isAmountEmptyMessage ? localized[amountError?.message] : ''}
                  isInErrorState={!!amountError?.severity}
                  label={localized['giftDetails.amount.input.textFieldLabel'] || ''}
                  onChange={e => updateGiftAmount(e.value)}
                  onBlur={() => {
                    logComponentEvent(
                      'GiftingAmount', 'CustomClicked',
                      { data: { amount: `$${selectedGiftAmount}` } }
                    );
                  }}
                  fixedDecimalScale
                  allowNegative={false}
                  decimalScale={2}
                />
              )}
            </div>
          </div>
        </div>
      </section>
      <section className='gift-details__message'>
        <p className='gift-details__message__label'>{localized['giftDetails.about.label'] || ''}</p>
        <p className='gift-details__message__description'>{localized['giftDetails.about.description'] || ''}</p>
        <TextField
          value={checkoutMessage}
          label={localized['giftDetails.message.input.label'] || ''}
          placeholder={localized['giftDetails.message.input.placeholder'] || ''}
          onChange={event => updateMessage(event.target.value)}
          isInErrorState={!!messageError?.severity}
          multiline
        />
        <div
          className={`
          gift-details__message__footer
          ${!!messageError.message && 'gift-details__message__footer--error'}
        `}
        >
          <span>{localized[messageError?.message] || ''}</span>
          <span>
            {checkoutMessage.length}/{MAXIMUM_CHARACTER_COUNT}
          </span>
        </div>
      </section>
      <Button
        size='large'
        variant='tonal'
        className='gift-details__checkout-btn'
        isLoading={isSubmitLoading}
        onClick={onDetailsNext}
      >
        {localized['continue'] || ''}
      </Button>
    </div>
  );
}
